import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useRouter } from 'next/router'

import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { type Locale } from '@lib/language'
import { getShopifyCartIdStorageKey } from '@lib/local-storage'
import { getLinkPageUrl } from '@lib/routes'
import { type LocalizedShop, ShopContext } from '@lib/shop-context'
import { emptyCart, getOrCreateCart } from './helpers'
import {
  useAddItemsToCart,
  useRemoveItemFromCart,
  useSubmitCart,
  useUpdateCartItem,
} from './hooks'
import {
  type Cart,
  type CartFormValues,
  type CartUpdateResult,
  type CartVariantLineItem,
  type SubmitCartResponse,
} from './types'

interface CartContextProps {
  cart: Cart
  isCartOpen: boolean
  isCartUpdating: boolean
  isCartProductAdding: boolean
  isCartSubmitting: boolean
  openCartInSeparatePage?: boolean
  openCartAfterAddingItems: boolean
  toggleCart: (newState?: boolean) => void
  addItemsToCart: (variantLineItems: CartVariantLineItem[]) => Promise<boolean>
  updateCartItem: (id: string, quantity: number) => Promise<CartUpdateResult>
  removeItemFromCart: (id: string) => Promise<boolean>
  submitCart: (values: CartFormValues) => Promise<SubmitCartResponse>
}

interface CartContextProviderProps {
  site: SanitySiteFragment
  localizedShop: LocalizedShop
  locale: Locale
  children: ReactNode
}

export const CartContext = createContext<CartContextProps>({
  cart: emptyCart,
  isCartOpen: false,
  isCartUpdating: false,
  isCartProductAdding: false,
  isCartSubmitting: false,
  openCartInSeparatePage: false,
  openCartAfterAddingItems: false,
  toggleCart: () => null,
  addItemsToCart: async () => false,
  updateCartItem: async () => ({}),
  removeItemFromCart: async () => false,
  submitCart: async () => ({
    errors: {},
  }),
})

/**
 * The cart context provider.
 */
export const CartContextProvider = ({
  site,
  localizedShop,
  locale,
  children,
}: CartContextProviderProps) => {
  const { shopifyStorefrontGraphQlClient } = useContext(ShopContext)

  const router = useRouter()

  const [cart, setCart] = useState<Cart>(emptyCart)
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [isCartUpdating, setIsCartUpdating] = useState(false)
  const [isCartProductAdding, setIsCartProductAdding] = useState(false)
  const [isCartSubmitting, setIsCartSubmitting] = useState(false)
  const [localeInitialised, setLocaleInitialised] = useState('')

  // Sanity settings
  const openCartInSeparatePage = useMemo(
    () => !!site.cartSettings?.openInSeparatePage,
    [site]
  )
  const openCartAfterAddingItems = useMemo(
    () => !!site.cartSettings?.openAfterAddingItems,
    [site]
  )

  // Update callbacks
  const saveCart = useCallback((locale: Locale, cart?: Cart) => {
    if (!cart) {
      return
    }

    setCart(cart)

    if (typeof window !== `undefined`) {
      const shopifyCartIdStorageKey = getShopifyCartIdStorageKey(locale)
      localStorage.setItem(shopifyCartIdStorageKey, cart.id)
    }
  }, [])

  const toggleCart = useCallback(
    (newState?: boolean) => {
      if (openCartInSeparatePage && newState === undefined) {
        const url = getLinkPageUrl('cartPage')
        router.push(url, url, {
          locale,
        })

        return
      }

      if (newState !== undefined) {
        setIsCartOpen(newState)

        return
      }

      setIsCartOpen(!isCartOpen)
    },
    [isCartOpen, locale, openCartInSeparatePage, router]
  )

  const addItemsToCart = useAddItemsToCart(
    cart,
    locale,
    setIsCartProductAdding,
    setIsCartUpdating,
    saveCart,
    toggleCart
  )
  const updateCartItem = useUpdateCartItem(
    cart,
    locale,
    setIsCartUpdating,
    saveCart
  )
  const removeItemFromCart = useRemoveItemFromCart(
    cart,
    locale,
    setIsCartUpdating,
    saveCart
  )
  const submitCart = useSubmitCart(cart, setIsCartSubmitting)

  // Load cart details
  useEffect(() => {
    const loadCart = async () => {
      if (
        !localizedShop.isLoadingFinished ||
        !localizedShop.shopifyShopAndCart ||
        localeInitialised === locale ||
        localizedShop.locale !== locale
      ) {
        return
      }

      if (!shopifyStorefrontGraphQlClient) {
        throw new Error('Shopify Storefront API client missing')
      }

      setLocaleInitialised(locale)

      const currentCart = await getOrCreateCart(
        locale,
        shopifyStorefrontGraphQlClient,
        localizedShop.shopifyShopAndCart
      )
      saveCart(locale, currentCart)
    }

    loadCart()
  }, [
    locale,
    localeInitialised,
    saveCart,
    localizedShop,
    shopifyStorefrontGraphQlClient,
  ])

  return (
    <CartContext.Provider
      value={{
        cart,
        isCartOpen,
        isCartUpdating,
        isCartProductAdding,
        isCartSubmitting,
        openCartInSeparatePage,
        openCartAfterAddingItems,
        toggleCart,
        addItemsToCart,
        updateCartItem,
        removeItemFromCart,
        submitCart,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}
